import classNames from "classnames";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import MobileHeader from "src/app/components/datatable/header/MobileHeader";
import TableHeader from "src/app/components/datatable/header/TableHeader";
import MobileActions from "src/app/components/datatable/miscellaneous/MobileActions";
import Pagination from "src/app/components/datatable/pagination/Pagination";
import { Icon } from "src/app/components/icon/Icon";
import Loader from "src/app/components/loader/Loader";
import DataFiltering from "src/app/modules/admin/components/projects/components/DataFiltering";
import { PROJECT_TABS } from "src/app/modules/admin/components/projects/constants";
import { TARGET_GROUPS } from "src/app/modules/auth/constants";
import projectCSS from "src/app/modules/user-projects/Projects.module.css";
import iconOptions from "src/assets/iconsGraphics/optionDots.svg";
import { ROUTES } from "src/config/constants";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { sortFieldGenerator } from "src/core/helpers/global/global";
import { Column } from "src/core/models/global";
import { ProjectResponse } from "src/core/models/projects";
import { ProjectsService } from "src/core/services/projects";
import { TableHead } from "src/types/global";

const headers: Array<TableHead> = [
  {
    name: "Šifra upita",
    field: "id",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Kreirao",
    field: "user.firstName,user.lastName",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Poslano Partneru",
    field: "partner.name",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Kreirano",
    field: "sysTimeCreated",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  { name: "", field: "", sortable: false, class: "u-w40 u-t-align-end u-t-uppercase u-t-secondary u-t-f16p", mclass: "u-mb20px u-t-f16p u-t-lh15" },
];

const Projects = ({
  activeTab,
  filteredPartnerList,
  filteredRegionList,
  filteredCityList,
  filteredSystemList,
  filteredTargetGroupList,
  filteredDateFrom,
  filteredDateTo,
  setFilteredPartnerList,
  setFilteredRegionList,
  setFilteredCityList,
  setFilteredSystemList,
  setFilteredTargetGroupList,
  setFilteredDateFrom,
  setFilteredDateTo,
}: {
  activeTab: string;
  filteredPartnerList: Array<number> | null;
  filteredRegionList: Array<number> | null;
  filteredCityList: Array<number> | null;
  filteredSystemList: Array<number> | null;
  filteredTargetGroupList: Array<number> | null;
  filteredDateFrom: Date | undefined;
  filteredDateTo: Date | undefined;
  setFilteredPartnerList: (value: number[]) => void;
  setFilteredRegionList: (value: number[]) => void;
  setFilteredCityList: (value: number[]) => void;
  setFilteredSystemList: (value: number[]) => void;
  setFilteredTargetGroupList: (value: number[]) => void;
  setFilteredDateFrom: (value: Date | undefined) => void;
  setFilteredDateTo: (value: Date | undefined) => void;
}) => {
  const [projects, setProjects] = useState<Array<ProjectResponse>>([]);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(6);
  const [numberOfPages, setNumberOfPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sorting, setSorting] = useState<Column>({ name: "sysTimeCreated", sortDescending: true });
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState<ProjectResponse>();
  const [headerMenuActive, setHeaderMenuActive] = useState(false);
  const headerMenuRef = useRef<Array<any>>([]);
  const setHeaderMenuRef = (key: number, ref: any) => {
    headerMenuRef.current[key] = ref;
  };
  const [actionsMenuActive, setActionsMenuActive] = useState(false);

  const actionsMenuRef = useRef<Array<any>>([]);
  const setActionsMenuRef = (key: number, ref: any) => {
    actionsMenuRef.current[key] = ref;
  };

  const modalData = useContext(ModalContext);

  useEffect(() => {
    const regionIds = filteredRegionList ? filteredRegionList : null;
    const cityIds = filteredCityList ? filteredCityList : null;
    const partnerIds = filteredPartnerList ? filteredPartnerList : null;
    const systemIds = filteredSystemList ? filteredSystemList : null;
    const targetGroups = filteredTargetGroupList ? filteredTargetGroupList.map((x: any) => TARGET_GROUPS.getText(x)!) : null;

    const dateFrom = filteredDateFrom ? filteredDateFrom.toDateString() : null;
    const dateTo = filteredDateTo ? filteredDateTo.toDateString() : null;

    if (loadingProjects) {
      if (regionIds || cityIds || partnerIds || dateFrom || dateTo) {
        const sort = sortFieldGenerator(sorting.name ?? "", sorting.sortDescending ?? false);
        ProjectsService.getFiltered(partnerIds, regionIds, cityIds, targetGroups, systemIds, dateFrom, dateTo, recordsPerPage, currentPage, sort)
          .then((response) => {
            if (response?.records) {
              setProjects(response.records);
              setRecordsPerPage(response.pageSize ?? 0);
              setNumberOfPages(response.totalNumOfPages ?? 0);
            }
          })
          .catch((e: ApiResult) => {
            if (!modalData?.content?.contentSet) {
              modalData?.triggerError(e);
            }
          })
          .finally(() => {
            setLoadingProjects(false);
          });
      } else {
        const sort = sortFieldGenerator(sorting.name ?? "", sorting.sortDescending ?? false);
        ProjectsService.get(recordsPerPage, currentPage, sort)
          .then((response) => {
            if (response?.records) {
              setProjects(response.records);
              setRecordsPerPage(response.pageSize ?? 0);
              setNumberOfPages(response.totalNumOfPages ?? 0);
            }
          })
          .catch((e: ApiResult) => {
            if (!modalData?.content?.contentSet) {
              modalData?.triggerError(e);
            }
          })
          .finally(() => {
            setLoadingProjects(false);
          });
      }
    }
  }, [
    loadingProjects,
    filteredPartnerList,
    filteredCityList,
    filteredRegionList,
    filteredSystemList,
    filteredTargetGroupList,
    filteredDateFrom,
    filteredDateTo,
    currentPage,
    sorting,
    recordsPerPage,
    modalData,
  ]);

  useEffect(() => {
    setLoadingProjects(true);
  }, [
    filteredPartnerList,
    filteredCityList,
    filteredRegionList,
    filteredSystemList,
    filteredTargetGroupList,
    filteredDateFrom,
    filteredDateTo,
    currentPage,
    sorting,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!headerMenuRef.current[0]?.contains(event.target as Node) && headerMenuActive) {
        setHeaderMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [headerMenuRef, headerMenuActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!actionsMenuRef.current[0]?.contains(event.target as Node) && actionsMenuActive) {
        setActionsMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [actionsMenuRef, actionsMenuActive]);

  return (
    <div className={classNames("u-col-lg-8", "u-border-box", "u-col-xs-12", projectCSS.listContainer)}>
      {/* Mobile projects HTML */}
      <div className={classNames(activeTab === PROJECT_TABS.PROJECTS ? "" : "u-none", projectCSS.mobileTabs)}>
        {headerMenuActive && (
          <MobileHeader
            setRef={setHeaderMenuRef}
            defaultSort={sorting}
            headers={headers.filter((head) => head.sortable)}
            onSorting={(column: Column) => {
              setSorting(column);
              setHeaderMenuActive(false);
            }}
          />
        )}
        {actionsMenuActive && (
          <MobileActions
            user={{}}
            project={selectedProject}
            isAdminLocation={true}
            handleDelete={() => {}}
            openProjectRename={() => {}}
            openPartnerModal={() => {}}
            downloadProject={() => {}}
            downloadSystemProjectPdf={() => {}}
            downloadSystemProjectExcel={() => {}}
            sendToMail={() => {}}
            setRef={setActionsMenuRef}
          />
        )}

        <div className={classNames("u-mb10px", projectCSS.inputs)}>
          <DataFiltering
            filteredPartnerList={filteredPartnerList}
            filteredRegionList={filteredRegionList}
            filteredCityList={filteredCityList}
            filteredSystemList={filteredSystemList}
            filteredTargetGroupList={filteredTargetGroupList}
            filteredDateFrom={filteredDateFrom}
            filteredDateTo={filteredDateTo}
            setFilteredPartnerList={setFilteredPartnerList}
            setFilteredRegionList={setFilteredRegionList}
            setFilteredCityList={setFilteredCityList}
            setFilteredSystemList={setFilteredSystemList}
            setFilteredTargetGroupList={setFilteredTargetGroupList}
            setFilteredDateFrom={setFilteredDateFrom}
            setFilteredDateTo={setFilteredDateTo}
          />
        </div>
        <div className={classNames("u-bg-background", projectCSS.containerMobile)}>
          <div
            onClick={() => setHeaderMenuActive(true)}
            className={classNames("u-flex u-flex-items-center u-mb20px u-col-xs-12 u-col-sm-12 u-cursor-pointer", projectCSS.mobileFilterCont)}
          >
            <label className="u-t-secondary u-mr10px u-t-f18p u-t-lh15">Poredaj po:</label>
            <span className="u-mr10px u-t-f18p u-t-lh15 u-t-lowercase">{headers.find((x) => x.field === sorting.name)?.name}</span>
            <Icon name={sorting.sortDescending ? "arrow-down" : "arrow-up"} className="u-t-f18p u-t-primary" />
          </div>
          <ul>
            {/* List item */}
            {!loadingProjects &&
              projects.map((project: ProjectResponse) => (
                <ListItem
                  key={project.id}
                  project={project}
                  openActions={() => {
                    setSelectedProject(project);
                    setActionsMenuActive(true);
                  }}
                />
              ))}
          </ul>
        </div>
      </div>
      {/* Desktop projects HTML */}
      <div className={classNames(projectCSS.desktopProject)}>
        <div className="u-box u-flex-wrap u-t-align-center u-pl5per u-pt20px">
          <div>
            <h1 className="u-t-primary u-t-uppercase">Upiti</h1>
          </div>
          <div className={classNames(projectCSS.inputs)}>
            <DataFiltering
              filteredPartnerList={filteredPartnerList}
              filteredRegionList={filteredRegionList}
              filteredCityList={filteredCityList}
              filteredSystemList={filteredSystemList}
              filteredTargetGroupList={filteredTargetGroupList}
              filteredDateFrom={filteredDateFrom}
              filteredDateTo={filteredDateTo}
              setFilteredPartnerList={setFilteredPartnerList}
              setFilteredRegionList={setFilteredRegionList}
              setFilteredCityList={setFilteredCityList}
              setFilteredSystemList={setFilteredSystemList}
              setFilteredTargetGroupList={setFilteredTargetGroupList}
              setFilteredDateFrom={setFilteredDateFrom}
              setFilteredDateTo={setFilteredDateTo}
            />
          </div>
          <div className={classNames(projectCSS.tableWrap)}>
            <table className="u-w100">
              <TableHeader defaultSort={sorting} headers={headers} onSorting={setSorting} />
              <tbody>
                {!projects.length && !loadingProjects && (
                  <tr className="u-col-12 u-t-halign-c">
                    <td colSpan={5}>Nema upita</td>
                  </tr>
                )}
                {!loadingProjects &&
                  projects.map((project: ProjectResponse) => (
                    <tr className="u-col-12" key={project.id}>
                      <td className="u-w15 u-t-align-start u-t-f16p">{project.id}</td>
                      <td className="u-w15 u-t-align-start u-t-f16p">{project.userName}</td>
                      <td className="u-w15 u-t-align-start u-t-f16p">{project.partnerName}</td>
                      <td className="u-w15 u-t-align-start u-t-f16p">{moment(project.created).format("DD.MM.yyyy")}</td>
                      <td className="u-w40 u-t-align-end u-t-f16p">
                        <Link
                          to={ROUTES.showAdminUserProject(project.userId, project.id)}
                          className={classNames("u-t-nodecoration", "u-t-w400", "u-cursor-pointer", projectCSS.blueFillBtn)}
                        >
                          Otvori
                        </Link>
                        <Link
                          to={ROUTES.showAdminUserProjects(project.userId)}
                          className={classNames("u-t-nodecoration", "u-t-w400", "u-cursor-pointer", projectCSS.whiteFillLink)}
                        >
                          Svi izračuni korisnika
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {loadingProjects && <Loader />}
      {activeTab === PROJECT_TABS.PROJECTS && (
        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} numberOfPages={numberOfPages} onPageChange={setCurrentPage} />
      )}
    </div>
  );
};

export default Projects;

const ListItem = ({ project, openActions }: { project: ProjectResponse; openActions: () => void }) => {
  let history = useHistory();

  function openProject() {
    history.push(ROUTES.showAdminUserProject(project.userId, project.id));
  }

  return (
    <li key={project.id}>
      <button onClick={() => openProject()} className={classNames("u-block", "u-w100", "u-t-nodecoration", "u-t-w400", "u-t-black", "u-cursor-pointer")}>
        <div className={classNames("u-bg-white", "u-flex", "u-flex-column", "u-mb5per", projectCSS.userProjects)}>
          <div className="u-flex u-col-12">
            <div className="u-flex u-flex-content-start u-col-5">
              <span className="u-t-secondary u-mr5px">šifra:</span>
              <span className="u-col-xs-5">{project.id}</span>
            </div>
            <div className="u-col-6 u-flex u-flex-content-end">
              <span className="u-t-secondary u-mr5px">kreirano:</span>
              <span className="u-t-align-end">{moment(project.created).format("DD.MM.yyyy")}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openActions();
              }}
              className="u-col-1 u-flex u-flex-content-end u-cursor-pointer"
            >
              <img alt="option button" src={iconOptions}></img>
            </button>
          </div>
          <div className="u-col-12 u-flex -u-flex-content-between u-mt5px u-mb5px">
            <h5 className="u-m0 u-t-w400 u-t-f18p u-t-lh15 u-m0"> {project.userName}</h5>
          </div>
          <div className="u-flex u-flex-content-start u-col-11">
            <span className="u-t-secondary u-col-6 u-t-align-start">Poslano partneru:</span>
            <span className="u-col-6 u-mlauto u-t-align-end">{project.partnerName}</span>
          </div>
        </div>
      </button>
    </li>
  );
};
